/**
 * Coupons Module
 *
 * Coupon validation before checkout.
 */

import { post } from '../core/client';
import { getShopId } from '../core/config';
import { getStore } from './store';
import { getCart } from './cart';
import type { SDKResponse, CouponValidation, CouponValidationOptions } from '../types';

async function resolveShopId(): Promise<string | null> {
  const cachedShopId = getShopId();
  if (cachedShopId) {
    return cachedShopId;
  }

  const storeResult = await getStore();
  if (!storeResult.success || !storeResult.data?.id) {
    return null;
  }

  return storeResult.data.id;
}

export async function validateCoupon(
  code: string,
  productOrOptions?: string | CouponValidationOptions
): Promise<SDKResponse<CouponValidation>> {
  const trimmedCode = code.trim();
  if (!trimmedCode) {
    return {
      success: false,
      message: 'Coupon code is required',
    };
  }

  const payload: Record<string, unknown> = {
    code: trimmedCode,
  };
  const productId = typeof productOrOptions === 'string'
    ? productOrOptions
    : productOrOptions?.productId;
  const variantId = typeof productOrOptions === 'string'
    ? undefined
    : productOrOptions?.variantId;

  if (variantId && !productId) {
    return {
      success: false,
      message: 'productId is required when variantId is provided',
    };
  }

  if (productId) {
    payload.product_id = productId;
    if (variantId) {
      payload.variant_id = variantId;
    }
  } else {
    const cart = getCart();
    if (cart.length === 0) {
      return {
        success: false,
        message: 'Cart is empty',
      };
    }

    const shopId = await resolveShopId();
    if (!shopId) {
      return {
        success: false,
        message: 'Failed to resolve store',
      };
    }

    payload.cart = JSON.stringify({
      shop_id: shopId,
      products: cart.map((item) => ({
        uniqid: item.product_id,
        quantity: item.quantity,
        variant_id: item.variant_id,
        price_variant_id: item.price_variant_id,
        addons: item.addons?.map((addon) => ({
          id: addon.id,
          quantity: addon.quantity ?? 1,
        })),
      })),
    });
  }

  const response = await post<CouponValidation>(
    '/v1/storefront/coupons/check',
    payload
  );

  return response;
}
